/* global __BUILD_TAG__ */
import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/client';

function showThematicMenuLinks(user) {
  if (!user) return false;
  return Boolean(
    user.is_student || user.is_teacher || user.is_staff || user.is_admin
  );
}

// Дата и время сборки (vite.config.js); в dev — подпись что это режим разработки
const BUILD_TAG = typeof __BUILD_TAG__ !== 'undefined' ? __BUILD_TAG__ : 'режим разработки (без сборки)';

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [topicMenu, setTopicMenu] = useState([]);

  useEffect(() => {
    if (!showThematicMenuLinks(user)) {
      setTopicMenu([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/topic-programs/menu/');
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) setTopicMenu(data);
      } catch {
        if (!cancelled) setTopicMenu([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch {
      navigate('/login');
    }
  }

  return (
    <div className="layout">
      <header className="header">
        <div className="header-inner">
          <NavLink to="/" className="logo">
            <span className="logo-icon">📘</span>
            <span className="logo-text">diobuch <span className="logo-version">v2.0</span></span>
          </NavLink>
          <nav className="nav">
            <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Главная
            </NavLink>
            {user?.is_student && (
              <NavLink to="/tasks" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Задачи
              </NavLink>
            )}
            {user && (
              <>
                {topicMenu.map((p) => (
                  <NavLink
                    key={p.slug}
                    to={`/themes/${p.slug}`}
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    {p.label || p.slug}
                  </NavLink>
                ))}
                {user?.is_student && (
                  <NavLink to="/my-teachers" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Мои преподаватели
                  </NavLink>
                )}
                {user?.is_student && (
                  <NavLink to="/topic-progress" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Прогресс по темам
                  </NavLink>
                )}
                {user?.is_student && (
                  <NavLink to="/my-attempts" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Мои попытки
                  </NavLink>
                )}
                {(user.is_teacher || user.is_staff || user.is_admin) && (
                  <NavLink to="/teacher" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Кабинет преподавателя
                  </NavLink>
                )}
                {user?.is_student && (
                  <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Мой прогресс
                  </NavLink>
                )}
                <NavLink to="/konspekty" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
                  Конспект
                </NavLink>
                <NavLink to="/collaborative" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                  Совместное кодирование
                </NavLink>
                {(user.is_teacher || user.is_staff || user.is_admin) && (
                  <NavLink to="/teacher/tasks" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Конструктор задач
                  </NavLink>
                )}
                {(user.is_teacher || user.is_staff || user.is_admin) && (
                  <NavLink to="/teacher/tags" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                    Теги
                  </NavLink>
                )}
                {(user.is_staff || user.is_admin) && (
                  <NavLink
                    to="/admin/topic-programs"
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    Программы (темы)
                  </NavLink>
                )}
              </>
            )}
          </nav>
          <div className="header-actions">
            <div className="header-theme-toggle" role="group" aria-label="Тема сайта">
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                title="Светлая тема сайта"
                aria-label="Светлая тема сайта"
              >
                <i className="fa-solid fa-sun ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${isDark ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                title="Тёмная тема сайта"
                aria-label="Тёмная тема сайта"
              >
                <i className="fa-solid fa-moon ui-icon" aria-hidden />
              </button>
            </div>
            {user ? (
              <div className="user-menu">
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    `user-name user-name-link${isActive ? ' user-name-link-active' : ''}`
                  }
                  title="Профиль"
                >
                  👤 {user.username}
                </NavLink>
                <button onClick={handleLogout} className="btn btn-outline btn-sm">
                  Выйти
                </button>
              </div>
            ) : (
              <div className="auth-links">
                <NavLink to="/login" className="btn btn-outline btn-sm">Войти</NavLink>
                <NavLink to="/register" className="btn btn-primary btn-sm">Регистрация</NavLink>
              </div>
            )}
          </div>
          <button className="mobile-menu-btn" onClick={() => {
            document.querySelector('.nav').classList.toggle('nav-open');
          }}>☰</button>
        </div>
      </header>
      <main className="main-content">
        <Outlet />
      </main>
      <footer className="footer">
        <p>© 2026 diobuch v2.0 — Подготовка к ЕГЭ/ОГЭ по информатике</p>
        <p className="footer-build" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
          Обновлено: {BUILD_TAG}
        </p>
      </footer>
    </div>
  );
}
